import DataLoader from "dataloader";
import {
  Endpoint,
  getEndpointURL,
} from "@gocardless/api/dashboard/common/endpoints";
import {
  UiComponentBootstrapRequestBody,
  UiComponentBootstrapResponseBody,
} from "@gocardless/api/dashboard/types";

import { ApiClient } from "@src/api";

type BootstrapKey = keyof UiComponentBootstrapResponseBody;
type BootstrapData = Partial<UiComponentBootstrapResponseBody>;

export const fetchBootstrapData = (
  api: ApiClient,
  keys: BootstrapKey[]
): Promise<BootstrapData> => {
  if (keys.length === 0) return Promise.resolve({});

  const requestBody: UiComponentBootstrapRequestBody = {
    bootstrap_data: keys,
  };

  return api.post<BootstrapData>(
    getEndpointURL(Endpoint.UiComponentBootstrap),
    {
      data: requestBody,
    }
  );
};

export const pick = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Partial<Pick<T, K>> =>
  keys.reduce<Partial<Pick<T, K>>>((result, key) => {
    if (key in obj) result[key] = obj[key];
    return result;
  }, {});

// A shared cache for bootstrap data. Each createComponent mount requests
// only the keys it needs. DataLoader batches concurrent requests into one
// call and caches the results, so components needing the same data avoid
// redundant network requests.
export const createBootstrapStore = (api: ApiClient) => {
  const loader = new DataLoader<BootstrapKey, BootstrapData[BootstrapKey]>(
    async (keys) => {
      try {
        const result = await fetchBootstrapData(api, keys as BootstrapKey[]);
        return keys.map((key) => result[key]);
      } catch (error) {
        keys.forEach((key) => loader.clear(key));
        throw error;
      }
    }
  );

  return {
    async request(keys: BootstrapKey[]): Promise<BootstrapData> {
      const values = await Promise.all(keys.map((key) => loader.load(key)));

      return keys.reduce<BootstrapData>((result, key, index) => {
        result[key] = values[index];
        return result;
      }, {});
    },
  };
};
